/**
 * SpeedyDrone | Variant-level Request a Quote (Minimog test theme).
 * Requires the data-sd-* markers in product sections.
 * Storefront presentation only; checkout validation remains a separate requirement.
 */
(() => {
  'use strict';

  // Main, featured and Quick View product sections can load this asset.
  if (window.__sdVariantQuoteRuntime) {
    window.__sdVariantQuoteRuntime.initialize();
    return;
  }

  const CONFIG_SELECTOR = 'script[data-sd-quote-variant-ids]';
  const ROOT_SELECTOR = '.m-main-product, .m-featured-product, .m-product-quickview';
  const STICKY_SELECTOR = '.m-sticky-addtocart, [data-view="sticky-atc"], sticky-atc';
  const instances = new WeakMap();
  const activeInstances = new Set();
  const stickyOriginalStyles = new WeakMap();
  const asId = (value) => String(value == null ? '' : value);

  function setVisible(element, visible) {
    element.hidden = !visible;
    if (visible) element.style.removeProperty('display');
    else element.style.setProperty('display', 'none', 'important');
  }

  function getPicker(instance) {
    return instance.root.querySelector('variant-picker');
  }

  function readFormVariantId(instance) {
    const input = instance.root.querySelector(
      'product-form.m-product-form--main [name="id"], product-form input[name="id"], form[action*="/cart/add"] [name="id"]'
    );
    return input ? asId(input.value) : '';
  }

  function resolveVariant(instance, variantId, variant) {
    if (variant && asId(variant.id) === variantId) return variant;
    const pickerVariant = getPicker(instance)?.currentVariant;
    if (pickerVariant && asId(pickerVariant.id) === variantId) return pickerVariant;
    const known = instance.variants.get(variantId);
    if (known) return known;
    // Product data may be populated after a theme-editor section is inserted.
    return window._themeProducts?.[instance.productId]?.variants?.find(
      (item) => asId(item.id) === variantId
    ) || null;
  }

  function updateSticky(instance, quoteRequired) {
    instance.root.querySelectorAll(STICKY_SELECTOR).forEach((element) => {
      if (quoteRequired) {
        if (!stickyOriginalStyles.has(element)) {
          const serverQuoteHidden = element.hasAttribute('data-sd-quote-sticky-hidden');
          stickyOriginalStyles.set(element, {
            display: serverQuoteHidden ? '' : element.style.getPropertyValue('display'),
            priority: serverQuoteHidden ? '' : element.style.getPropertyPriority('display'),
            hidden: serverQuoteHidden ? false : element.hidden
          });
        }
        setVisible(element, false);
      } else if (stickyOriginalStyles.has(element)) {
        const original = stickyOriginalStyles.get(element);
        element.hidden = original.hidden;
        if (original.display) element.style.setProperty('display', original.display, original.priority);
        else element.style.removeProperty('display');
        element.removeAttribute('data-sd-quote-sticky-hidden');
        stickyOriginalStyles.delete(element);
      } else if (element.hasAttribute('data-sd-quote-sticky-hidden')) {
        // The picker may already have selected a standard variant before initialization.
        element.hidden = false;
        element.style.removeProperty('display');
        element.removeAttribute('data-sd-quote-sticky-hidden');
      }
    });
    instance.root.querySelectorAll('sticky-atc').forEach((sticky) => {
      sticky.setStickyAddToCartHeight?.();
    });
  }

  function updateQuoteFields(instance, variantId, variant) {
    const isInitial = variantId === instance.initial.id;
    const values = {
      id: variantId,
      title: variant?.title ?? (isInitial ? instance.initial.title : ''),
      sku: variant?.sku ?? (isInitial ? instance.initial.sku : '')
    };
    Object.entries(values).forEach(([field, value]) => {
      instance.root.querySelectorAll(`[data-sd-quote-variant-${field}]`).forEach((input) => {
        input.value = value;
      });
    });
    instance.root.querySelectorAll('[data-sd-quote-link]').forEach((link) => {
      if (!variantId || !link.dataset.productUrl) return;
      const url = new URL(link.dataset.productUrl, window.location.origin);
      url.searchParams.set('variant', variantId);
      link.href = url.href;
    });
  }

  function applyVariant(instance, variantId, variant) {
    const quoteRequired = instance.quoteIds.has(variantId);
    const selected = resolveVariant(instance, variantId, variant);
    instance.currentVariantId = variantId;
    instance.isQuote = quoteRequired;
    instance.root.querySelectorAll('[data-sd-quote-state]').forEach((block) => {
      block.setAttribute('data-sd-quote-state', String(quoteRequired));
    });
    instance.root.querySelectorAll('[data-sd-standard-price], [data-sd-price-tax], [data-sd-payment-terms], [data-sd-purchase-panel]')
      .forEach((element) => setVisible(element, !quoteRequired));
    instance.root.querySelectorAll('[data-sd-quote-price], [data-sd-quote-panel]')
      .forEach((element) => setVisible(element, quoteRequired));
    updateQuoteFields(instance, variantId, selected);
    updateSticky(instance, quoteRequired);
  }

  function dispose(instance) {
    window.clearTimeout(instance.timer);
    instance.abort.abort();
    updateSticky(instance, false);
    activeInstances.delete(instance);
    instances.delete(instance.config);
  }

  function pruneInstances() {
    activeInstances.forEach((instance) => {
      if (!instance.config.isConnected || !instance.root.isConnected) dispose(instance);
    });
  }

  function findVariant(detail) {
    return detail.variant || detail.selectedVariant || detail.currentVariant ||
      detail.current_variant || (detail.id != null ? detail : null);
  }

  function handleVariantEvent(event) {
    pruneInstances();
    const detail = event.detail || {};
    const variant = findVariant(detail);
    const variantId = asId(variant?.id);
    activeInstances.forEach((instance) => {
      const picker = getPicker(instance);
      const source = detail.picker || (event.target?.matches?.('variant-picker') ? event.target : null);
      if (source) {
        // Quick View and another featured section each own a different picker.
        if (source !== picker) return;
      } else {
        const productId = asId(detail.productId || detail.product_id || detail.product?.id);
        if (productId && productId !== instance.productId) return;
        const sectionId = asId(detail.sectionId || detail.section_id);
        if (sectionId && sectionId !== instance.sectionId) return;
        // Native integration events are dispatched on document without a source.
        // Only accept the variant already selected by this section's own form.
        if (!variantId || variantId !== readFormVariantId(instance)) return;
      }
      applyVariant(instance, variantId, variant);
    });
  }

  function scheduleFormUpdate(instance, event) {
    const target = event.target;
    if (!target?.matches?.('[name="id"]') && !target?.closest?.('variant-picker, sticky-atc')) return;
    window.clearTimeout(instance.timer);
    instance.timer = window.setTimeout(() => {
      if (!instance.config.isConnected) return;
      const id = readFormVariantId(instance);
      if (id !== instance.currentVariantId) applyVariant(instance, id, null);
    }, 0);
  }

  function isCartAddForm(form) {
    return form?.tagName === 'FORM' && (
      /\/cart\/add(?:\.js)?(?:\?|$)/.test(form.getAttribute('action') || '') ||
      Boolean(form.closest('product-form'))
    );
  }

  function blockQuoteCartAction(event, form) {
    if (!isCartAddForm(form)) return;
    pruneInstances();
    const variantId = asId(form.querySelector('[name="id"]')?.value);
    for (const instance of activeInstances) {
      const formRoot = form.closest(ROOT_SELECTOR) || form.closest('.shopify-section');
      if (instance.quoteIds.has(variantId) || (instance.isQuote && formRoot === instance.root)) {
        event.preventDefault();
        event.stopImmediatePropagation();
        return;
      }
    }
  }

  function initialize() {
    pruneInstances();
    document.querySelectorAll(CONFIG_SELECTOR).forEach((config) => {
      if (instances.has(config)) return;
      let ids;
      try {
        ids = JSON.parse(config.textContent || '[]');
      } catch (error) {
        console.warn('[SpeedyDrone Quote] Invalid quote variant JSON', error);
        return;
      }
      if (!Array.isArray(ids) || !ids.length) return;
      const root = config.closest(ROOT_SELECTOR) || config.closest('.shopify-section') || config.parentElement;
      if (!root) return;
      const quotePanel = root.querySelector('[data-sd-quote-panel]');
      const productId = asId(config.dataset.productId || quotePanel?.dataset.productId);
      let productData = window._themeProducts?.[productId];
      const dataNode = root.querySelector('script[id="productData"][type="application/json"]');
      if (dataNode) {
        try {
          productData = JSON.parse(dataNode.textContent);
        } catch (_error) {
          // The existing global product data remains a usable fallback.
        }
      }
      const instance = {
        config,
        root,
        productId,
        sectionId: asId(root.dataset.sectionId || getPicker({ root })?.dataset.sectionId || root.closest('.shopify-section')?.id.replace(/^shopify-section-/, '')),
        quoteIds: new Set(ids.map(asId).filter(Boolean)),
        variants: new Map((productData?.variants || []).map((variant) => [asId(variant.id), variant])),
        currentVariantId: '',
        isQuote: false,
        abort: new AbortController(),
        timer: null,
        initial: {
          id: asId(config.dataset.initialVariantId),
          title: root.querySelector('[data-sd-quote-variant-title]')?.value || '',
          sku: root.querySelector('[data-sd-quote-variant-sku]')?.value || ''
        }
      };
      instances.set(config, instance);
      activeInstances.add(instance);
      // Respect a picker that initialized before this deferred script, including
      // its explicit empty selection when no option combination is selected.
      const picker = getPicker(instance);
      const initialId = picker && 'currentVariant' in picker
        ? asId(picker.currentVariant?.id) : (readFormVariantId(instance) || instance.initial.id);
      applyVariant(instance, initialId, picker?.currentVariant);
      ['input', 'change'].forEach((type) => {
        root.addEventListener(type, (event) => scheduleFormUpdate(instance, event), { signal: instance.abort.signal });
      });
    });
  }

  window.__sdVariantQuoteRuntime = { initialize };
  document.addEventListener('sd:variant-change', handleVariantEvent);
  document.addEventListener('variant:changed', handleVariantEvent);
  document.addEventListener('submit', (event) => blockQuoteCartAction(event, event.target), true);
  document.addEventListener('click', (event) => {
    const button = event.target?.closest?.('[name="add"], [data-add-to-cart]');
    if (button) blockQuoteCartAction(event, button.closest('form'));
  }, true);
  document.addEventListener('shopify:section:load', initialize);
  document.addEventListener('quick-view:loaded', initialize);
  document.addEventListener('shopify:section:unload', (event) => {
    activeInstances.forEach((instance) => {
      if (event.target === instance.root || event.target?.contains?.(instance.config)) dispose(instance);
    });
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initialize, { once: true });
  } else {
    initialize();
  }
})();
